import React from 'react';
import { SkeletonLoader } from '../';
import { useTooltip, useGlobalState } from '../../hooks';

/**
 * Renders one labelled detail of an asset, with an optional tooltip on its heading.
 *
 * The tooltip is resolved in this priority order:
 * 1. An explicit `tooltip` string passed by the caller.
 * 2. An automatic fallback from the `displayNames` global state, keyed by `property`:
 *    `displayNames[property]?.display_name_description`.
 *
 * The value is taken from `data`, or from `children` when it needs to be composed
 * (e.g. a link or an icon alongside the text).
 *
 * @param {object} props
 * @param {string} [props.label]                        - Heading text for the detail.
 * @param {React.ReactNode} [props.data]                - The value to render.
 * @param {string} [props.tooltip]                      - Tooltip text shown on hovering the heading.
 * @param {string} [props.property]                     - Name of the property this detail shows, used as displayNames lookup key for the tooltip fallback.
 * @param {boolean} [props.loading]                     - Renders a skeleton in place of the heading while true.
 * @param {boolean} [props.showLoadingForChildren=false] - Also renders a skeleton in place of the value while loading.
 * @param {number} [props.loadingChildrenCount=1]       - Number of skeleton rows to render for the value.
 * @param {React.ReactNode} [props.children]            - The value to render when `data` is not given.
 */
export const ObjectDetail = ({
  label,
  data,
  tooltip,
  property,
  loading,
  showLoadingForChildren = false,
  loadingChildrenCount = 1,
  children,
}) => {
  const { showTooltip, hideTooltip } = useTooltip();
  const [displayNames] = useGlobalState('displayNames');

  tooltip = tooltip || (property && displayNames[property]?.display_name_description);

  return (
    <div className={'ObjectDetail'}>
      <h3
        onMouseEnter={() => tooltip && showTooltip({ title: tooltip })}
        onMouseLeave={tooltip && hideTooltip}
      >
        {!loading ? label : <SkeletonLoader width="5vw" />}
      </h3>

      <div className="display-linebreak">
        {showLoadingForChildren && loading ? (
          <SkeletonLoader width="10vw" count={loadingChildrenCount} />
        ) : (
          data || children
        )}
      </div>
    </div>
  );
};
